import React, { ReactElement, ReactNode, useEffect, Suspense } from 'react';
import ErrorBoundary from './ErrorBoundary';

type SuspensefulErrorBoundaryProps = {
  errorFallback: (error: Error) => ReactElement;
  loadingFallback: ReactElement;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  children: ReactNode;
};

/*
 * A component that can be used around suspenseful components to both catch errors and render loading indicators, with callbacks for when loading is finished or an error occurs
 */

export default function SuspensefulErrorBoundary(
  props: SuspensefulErrorBoundaryProps
): ReactElement {
  const { errorFallback, loadingFallback, onLoad, onError, children } = props;

  return (
    <ErrorBoundary
      fallback={error => {
        onError && onError(error);
        return errorFallback(error);
      }}
    >
      <Suspense fallback={loadingFallback}>
        {(() => {
          onLoad && onLoad();
          return children;
        })()}
      </Suspense>
    </ErrorBoundary>
  );
}

function Loader({
  onLoad,
  loadingFallback
}: Pick<SuspensefulErrorBoundaryProps, 'onLoad' | 'loadingFallback'>): ReactElement {
  useEffect(() => {
    if (onLoad) {
      return () => {
        onLoad();
      };
    }
    // We only want to run this on unmount
    // The downside is that we ignore updates to onLoad, but oh well
    // Hopefully the user doesn't expect us to run the latest function they pass in,
    //  cause we're only running the one they pass in on mount
  }, []);

  return loadingFallback;
}
